import React, { useState } from 'react';
import { ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { PasswordChangeConfirmation } from '@/components/ui/PasswordChangeConfirmation';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';
import { userService } from '@/services/userService';
import { useUser } from '@/contexts/UserContext';
import type { User } from '@/types/user';

interface PasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

interface PasswordValidation {
  hasMinLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
}

export const UpdatePasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const { updateUser } = useUser();
  const [passwordData, setPasswordData] = useState<PasswordData>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const validatePassword = (password: string): PasswordValidation => {
    return {
      hasMinLength: password.length >= 8,
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password)
    };
  };

  const validation = validatePassword(passwordData.newPassword);
  const isValidPassword = validation.hasMinLength && validation.hasUppercase && validation.hasLowercase;
  const passwordsMatch = passwordData.newPassword === passwordData.confirmPassword;

  const handleInputChange = (field: keyof PasswordData, value: string) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isValidPassword) {
      toast({
        title: "Invalid Password",
        description: "Please ensure your password meets all requirements.",
        variant: "destructive"
      });
      return;
    }

    if (!passwordsMatch) {
      toast({
        title: "Passwords Don't Match",
        description: "Please ensure both password fields match.",
        variant: "destructive"
      });
      return;
    }

    // Show confirmation modal instead of directly submitting
    setShowConfirmationModal(true);
  };

  const handleConfirmPasswordChange = async () => {
    setIsLoading(true);
    try {
      const response = await userService.updatePassword({
        current_password: passwordData.currentPassword,
        password: passwordData.newPassword,
        password_confirmation: passwordData.confirmPassword,
      });

      // Update tokens
      userService.updateTokens(response);

      // Update user context if user data is returned
      if (response.user) {
        const updatedUser: User = {
          id: response.user.id,
          email: response.user.email,
          name: `${response.user.first_name || ''} ${response.user.last_name || ''}`.trim(),
          first_name: response.user.first_name,
          last_name: response.user.last_name,
          phone_code: response.user.phone_code || response.user.mobile_region_code,
          phone: response.user.phone || response.user.mobile_number,
          country: response.user.country,
          gender: response.user.gender,
          dob: response.user.dob,
          mobile_number: response.user.mobile_number,
          mobile_region_code: response.user.mobile_region_code,
          fk_role_id: response.user.fk_role_id,
          role: response.user.fk_role_id === 3 ? 'plus' : 'visitor',
          isAuthenticated: true,
          profile: response.user,
        };
        updateUser(updatedUser);
      }

      toast({
        title: "Password Updated",
        description: "Your password has been successfully updated.",
      });
      
      setShowConfirmationModal(false);
      navigate('/profile');
    } catch (error: any) {
      console.error('Failed to update password:', error);
      const errorMessage = error.response?.data?.message || error.response?.data?.errors?.password?.[0] || "Failed to update password. Please try again.";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseModal = () => {
    if (!isLoading) {
      setShowConfirmationModal(false);
    }
  };

  const handleBack = () => {
    navigate('/profile');
  };

  return (
    <div className="min-h-screen relative">
      {/* Background */}
      
      {/* Animated background particles */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-white rounded-full animate-pulse opacity-60"></div>
        <div className="absolute top-1/3 right-1/3 w-1 h-1 bg-blue-300 rounded-full animate-pulse opacity-40 delay-1000"></div>
        <div className="absolute bottom-1/4 left-1/3 w-1.5 h-1.5 bg-purple-300 rounded-full animate-pulse opacity-50 delay-2000"></div>
        <div className="absolute top-2/3 right-1/4 w-1 h-1 bg-white rounded-full animate-pulse opacity-30 delay-3000"></div>
      </div>

      {/* Breadcrumb */}
      <div className="relative z-10 pt-6 px-6">
        <div className="text-white text-sm text-left">
          <span className="opacity-70">Home</span>
          <span className="mx-2">/</span>
          <span className="opacity-70">My Profile</span>
          <span className="mx-2">/</span>
          <span>Update Password</span>
        </div>
      </div>

      <h1 className="text-white text-3xl font-bold text-center mt-8 mb-8">
            Update Your Password
          </h1>

      {/* Main Content */}
      <div className="relative z-10 flex items-center justify-center min-h-screen px-6">
        <div className="update-password-container">
          {/* Back Button */}
          <button
            onClick={handleBack}
            className="update-password-back-button"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </button> 

          {/* Lock Icon */}
          <div className="flex justify-center mb-8">
            <img src="/lock.png" alt="Lock" className="w-82 h-106" />
          </div>

          {/* Form Container */}
          <div className="update-password-form-container">
            <form onSubmit={handleSubmit} className="h-full flex flex-col">
              <div className="space-y-3 text-left flex-1">
                {/* Current Password */}
                  <div className="space-y-1">
                    <label className="text-white font-medium text-sm">Current Password</label>
                    <div className="relative">
                      <PasswordInput
                        value={passwordData.currentPassword}
                        onChange={(value) => handleInputChange('currentPassword', value)}
                        showPassword={showPasswords.current}
                        className="update-password-input"
                        placeholder="Enter current password"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => togglePasswordVisibility('current')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                {/* New Password */}
                <div className="space-y-1">
                  <label className="text-white font-medium text-sm">New Password</label>
                  <div className="relative">
                    <PasswordInput
                      value={passwordData.newPassword}
                      onChange={(value) => handleInputChange('newPassword', value)}
                      showPassword={showPasswords.new}
                      className="update-password-input"
                      placeholder="Enter new password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('new')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  
                  {/* Password Requirements */}
                  {passwordData.newPassword && (
                    <div className="space-y-1 text-xs mt-1">
                      <div className={`flex items-center ${validation.hasMinLength ? 'text-green-600' : 'text-red-600'}`}>
                        <img 
                          src={validation.hasMinLength ? "/green_check_vector.svg" : "/red_cross_vector.svg"} 
                          alt={validation.hasMinLength ? "Valid" : "Invalid"} 
                          className="w-3 h-3 mr-2" 
                        />
                        contains at least 8 characters
                      </div>
                      <div className={`flex items-center ${validation.hasUppercase ? 'text-green-600' : 'text-red-600'}`}>
                        <img 
                          src={validation.hasUppercase ? "/green_check_vector.svg" : "/red_cross_vector.svg"} 
                          alt={validation.hasUppercase ? "Valid" : "Invalid"} 
                          className="w-3 h-3 mr-2" 
                        />
                        contains at least one capital letter
                      </div>
                      <div className={`flex items-center ${validation.hasLowercase ? 'text-green-600' : 'text-red-600'}`}>
                        <img 
                          src={validation.hasLowercase ? "/green_check_vector.svg" : "/red_cross_vector.svg"} 
                          alt={validation.hasLowercase ? "Valid" : "Invalid"} 
                          className="w-3 h-3 mr-2" 
                        />
                        contains at least one lowercase letter
                      </div>
                    </div>
                  )}
                </div>

                {/* Confirm New Password */}
                <div className="space-y-1">
                  <label className="text-white font-medium text-sm">Confirm New Password</label>
                  <div className="relative">
                    <PasswordInput
                      value={passwordData.confirmPassword}
                      onChange={(value) => handleInputChange('confirmPassword', value)}
                      showPassword={showPasswords.confirm}
                      className="update-password-input"
                      placeholder="Confirm new password"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => togglePasswordVisibility('confirm')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  {passwordData.confirmPassword && !passwordsMatch && (
                    <p className="text-red-600 text-xs">Passwords do not match</p>
                  )}
                </div>
              </div>

              {/* Submit Button - Always at bottom */}
              <div className="mt-6">
                <Button
                  type="submit"
                  disabled={isLoading || !isValidPassword || !passwordsMatch}
                  className="update-password-submit-button"
                >
                  Change Password
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Password Change Confirmation Modal */}
      <PasswordChangeConfirmation
        isOpen={showConfirmationModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirmPasswordChange}
        isLoading={isLoading}
      />
    </div>
  );
};
